// Device charts (device page): GSM signal, power (supply and backup battery, both volts, so one
// axis) and GPS (satellites, plus the records with a GPS problem), over 24 hours or 7 days.
import { useEffect, useState } from 'react';
import { DeviceSeries, trackers } from '../api';
import { dayLabel, time } from '../format';
import { TimeChart } from './TimeChart';

const PERIODS = [['24h', 'Last 24 h', 24], ['7d', '7 days', 168]] as const;
type Period = (typeof PERIODS)[number][0];

export function DeviceCharts({ trackerId }: { trackerId: number }) {
  const [period, setPeriod] = useState<Period>('24h');
  const [data, setData] = useState<(DeviceSeries & { from: number; to: number }) | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    const to = Date.now();
    const from = to - PERIODS.find((p) => p[0] === period)![2] * 3600_000;
    trackers.series(trackerId, new Date(from).toISOString(), new Date(to).toISOString())
      .then((d) => { if (live) { setData({ ...d, from, to }); setError(''); } }).catch((e) => live && setError((e as Error).message));
    return () => { live = false; };
  }, [trackerId, period]);

  return (
    <section className="card form charts-box" aria-label="Charts">
      <div className="page-head">
        <h2>Charts</h2>
        <div className="seg" role="group" aria-label="Period">
          {PERIODS.map(([k, label]) => <button key={k} type="button" aria-pressed={period === k} onClick={() => setPeriod(k)}>{label}</button>)}
        </div>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {!data && !error && <p className="muted">Loading…</p>}
      {data && (
        <>
          <TimeChart title="GSM signal" unit="/ 5" decimals={0} yMin={0} yMax={5} step series={[{ name: 'Signal', points: data.gsm }]}
            from={data.from} to={data.to} empty="The tracker sent no signal strength (IO 21) in this period." />
          <TimeChart title="Power" unit="V" decimals={2} yMin={0}
            series={[{ name: 'Supply', points: data.power }, ...(data.battery.length ? [{ name: 'Backup battery', points: data.battery }] : [])]}
            from={data.from} to={data.to} empty="No voltage readings in this period." />
          <TimeChart title="GPS satellites" unit="" decimals={0} yMin={0} step series={[{ name: 'Satellites', points: data.satellites }]}
            from={data.from} to={data.to} empty="No GPS readings in this period." />
          <div className="gps-errors">
            <p className="small">
              <b>GPS problems:</b> {data.gpsErrors.count === 0 ? 'none' : <>
                {data.gpsErrors.count.toLocaleString('en-IE')} of {data.records.toLocaleString('en-IE')} records
                ({data.gpsErrors.invalid.toLocaleString('en-IE')} without a valid fix, the rest with fewer than 4 satellites)
              </>}
            </p>
            {data.gpsErrors.latest.length > 0 && (
              <details>
                <summary>Newest {data.gpsErrors.latest.length}</summary>
                <ul className="small">
                  {data.gpsErrors.latest.map((e) => (
                    <li key={e.t}>{dayLabel(e.t)} {time(e.t)} · {e.valid ? `${e.satellites} satellites` : 'no valid fix'}{e.satellites !== null && !e.valid ? ` (${e.satellites} satellites)` : ''}</li>
                  ))}
                </ul>
              </details>
            )}
            <p className="muted small">Trackers in sleep mode switch GPS off on purpose, so quiet hours with no fix are normal for parked vehicles.</p>
          </div>
        </>
      )}
    </section>
  );
}
